<template>
  <PageView>
    <h1
      v-if="applicationVersion"
      :aria-label="$t('titles.application-update', { name: applicationName })"
      class="title main-title"
    >
      {{ $t("titles.application-update", {name: applicationName}) }}
    </h1>
    <h1
      v-else
      :aria-label="$t('titles.application-creation')"
      class="title main-title"
    >
      {{ $t("titles.application-creation") }}
    </h1>
    <div class="card submissionListVisible">
      <Form
        ref="observer"
        v-slot="{ handleSubmit }"
      >
        <b-steps
          v-model="activeStep"
          size="is-medium"
        >
          <b-step-item icon="file-circle-plus">
            <h1
              :aria-label="$t('dataTypesRepository.choose-file')"
              class="titleSubmissionListVisible has-text-centered"
            >
              {{ $t("dataTypesRepository.choose-file") }}
              <sup class="supInfo">
                <b-tooltip
                  :label="$t('applications.help_config')"
                  position="is-right"
                >
                  <a
                    :aria-label="$t('applications.help_config')"
                    class="linkInfo"
                    @click="showHelp"
                  >
                    <b-icon icon="question-circle" />
                  </a>
                </b-tooltip>
              </sup>
            </h1>
            <Field
              v-slot="{ errors, valid, field }"
              name="applicationCreation"
              rules="required"
              vid="applicationCreation"
            >
              <b-field
                :type="{
                  'is-danger': errors && errors.length > 0,
                  'is-success': valid,
                }"
                class="columns"
              >
                <b-upload
                  v-model="applicationConfigFile.file"
                  :aria-label="$t('dataTypesRepository.choose-file')"
                  accept=".yaml, .zip, .yml"
                  class="column is-offset-3 is-6 uploadDragDrop"
                  drag-drop
                  expanded
                  @blur="field.onBlur"
                  @input="(file) => field.onChange(file)"
                >
                  <section>
                    <div class="content has-text-centered">
                      <p>
                        <b-icon
                          icon="upload"
                          size="is-large"
                        />
                      </p>
                      <p>{{ $t("dataTypesRepository.choose-file") }}</p>
                    </div>
                  </section>
                </b-upload>
              </b-field>
              <b-tag
                v-if="applicationConfigFile.file"
                :aria-label="applicationConfigFile.file.name"
                class="columns"
                icon="file"
                size="is-medium"
              >
                {{ applicationConfigFile.file.name }}
              </b-tag>
            </Field>
          </b-step-item>
          <b-step-item icon="file-circle-check">
            <h1
              :aria-label="$t('applications.test')"
              class="titleSubmissionListVisible has-text-centered"
            >
              {{ $t("applications.test") }}
            </h1>
            <b-progress
              v-if="counter < 1"
              :value="counter * 100"
              show-value
              size="is-medium"
              type="is-primary"
            >
              <span v-if="isLoading">
                <LoadingAnimate :size="'is-small'" />
              </span>
              <span v-else> {{ counter.toFixed(2) * 100 }}% </span>
            </b-progress>
            <div
              v-else
              class="columns"
            >
              <b-button
                :aria-label="$t('applications.test')"
                class="column is-2 has-text-centered bigBtnValidate"
                outlined
                size="is-large"
                type="is-primary"
                @click="handleSubmit(testApplication)"
              >
                <div class="content has-text-centered">
                  <p>
                    <b-icon
                      class="bigIconValidate"
                      icon="vial"
                      size="is-large"
                    />
                  </p>
                </div>
              </b-button>
            </div>
            <div v-if="errorsMessages.length !== 0">
              <ShowErrors
                :errors-messages="errorsMessages"
                :errors-messages-length="errorsMessages.length"
              />
            </div>
          </b-step-item>
          <b-step-item icon="floppy-disk">
            <h1
              :aria-label="$t('applications.test')"
              class="titleSubmissionListVisible has-text-centered"
            >
              {{ $t("applications.test") }}
            </h1>
            <Field
              v-slot="{ errors, valid, field }"
              class="column"
              name="applicationsName"
              vid="applicationsName"
            >
              <b-field
                :message="errors[0]"
                :type="{
                  'is-danger': errors && errors.length > 0,
                  'is-success': valid,
                }"
                class="input-field"
              >
                <template #label>
                  {{ $t("applications.name") }}
                  <span class="mandatory">
                    {{ $t("validation.obligatoire") }}
                  </span>
                </template>

                <b-input
                  v-model="applicationConfigFile.name"
                  :aria-label="$t('applications.name-placeholder')"
                  :placeholder="$t('applications.name-placeholder')"
                  maxlength="43"
                  @blur="field.onBlur"
                  @input="field.onChange($event)"
                />
              </b-field>
            </Field>
            <b-field
              :aria-label="$t('dataTypesRepository.comment')"
              :label="$t('dataTypesRepository.comment')"
              class="column"
              expanded
            >
              <b-input
                v-model="applicationConfigFile.comment"
                :aria-label="applicationConfigFile.comment"
                maxlength="200"
                type="textarea"
              />
            </b-field>
          </b-step-item>
          <template #navigation="{ previous, next }">
            <div class="columns">
              <div class="column is-2">
                <b-button
                  :disabled="previous.disabled"
                  aria-label="previous"
                  expanded
                  icon-left="chevron-left"
                  @click.prevent="previous.action"
                />
              </div>
              <div class="column is-offset-8 is-2">
                <b-button
                  v-if="(btnUpdateConfig || applicationVersion || applicationName) && next.disabled"
                  :disabled="
                    applicationConfigFile.name === '' ||
                      applicationConfigFile.name !== applicationName ||
                      disabled
                  "
                  aria-label="update"
                  expanded
                  icon-left="edit"
                  type="is-warning"
                  @click="handleSubmit(changeConfiguration)"
                />
                <b-button
                  v-else-if="applicationConfigFile.file && applicationConfigFile.name !== '' && next.disabled"
                  :disabled="disabled"
                  aria-label="create"
                  expanded
                  icon-left="plus"
                  type="is-dark"
                  @click="handleSubmit(createApplication)"
                />
                <b-button
                  v-else-if="activeStep === 1 && applicationConfigFile.file"
                  :disabled="!validate"
                  aria-label="next"
                  expanded
                  icon-left="chevron-right"
                  @click="next.action"
                />
                <b-button
                  v-else
                  :disabled="!applicationConfigFile.file"
                  aria-label="next"
                  expanded
                  icon-left="chevron-right"
                  @click="next.action"
                />
              </div>
            </div>
          </template>
        </b-steps>
      </Form>
    </div>
  </PageView>
</template>

<script lang="ts">
import PageView from "@/views/common/PageView.vue";
import { Form, Field } from "vee-validate";
import { HttpStatusCodes } from "@/utils/HttpUtils";
import LoadingAnimate from "@/components/common/LoadingAnimate.vue";
import ShowErrors from "@/components/application/ShowErrors.vue";
import useBoolean from "@/composable/components/boolean";
import useObject from "@/composable/components/object";
import useArray from "@/composable/components/array";
import useNumber from "@/composable/components/number";
import services from "@/composable/services";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { computed, inject, ref, watch } from "vue";
import { BButton, BField, BIcon, BInput, BProgress, BStepItem, BSteps, BTag, BTooltip, BUpload } from "buefy";
import "@/style/views/application/ApplicationCreationView.scss";
import { parseResultChunck } from "@/services/validation/ApplicationValicationService";
import { ApplicationConfigFile } from "@/types/front/ApplicationConfigFile";
import { ErrorType } from "@/composable/application/errors";

export default {
  name: "ApplicationCreationView",
  components: {
    BStepItem,
    BSteps,
    BUpload,
    BIcon,
    BTooltip,
    BProgress,
    BTag,
    BInput,
    BField,
    BButton,
    ShowErrors,
    LoadingAnimate,
    PageView,
    Form,
    Field,
  },
  props: {
    applicationNames: {
      type: Array,
    },
    applicationName: {
      type: String,
      default: "",
    },
    applicationVersion: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const router = useRouter();
    const { reactiveObject: applicationConfigFile } = useObject(new ApplicationConfigFile());
    const { refBoolean: btnUpdateConfig, doChangeBoolean: changeBtnUpdateConfig } = useBoolean();
    const { shallowRefArray: errorsMessages, doChangeArray: changeErrorsMessages } = useArray();
    const activeStep = ref(0);
    const { shallowRefArray: infos } = useArray();
    const { shallowRefArray: errors } = useArray();
    const currentUser = services.loginService.getAuthenticatedUser();
    let disabled = useBoolean().refBoolean;
    let validate = useBoolean().refBoolean;
    let counter = useNumber(1).refNumber;
    let isLoading = computed(() => counter.value === 0);
    const { onUploadComplete, onUploadProgress, startUpload, finishUpload } = inject("fileUpload");

    async function saveApplication(status) {
      counter.value = 0;
      watch(counter, value => value);
      let canBeShowAndReturnErrorsMessage = [];
      startUpload(true);
      try {
        let method;
        switch (status) {
          case "create":
            method = (chunk) => {
              return parseChunck(chunk, "create", canBeShowAndReturnErrorsMessage);
            };
            await services.applicationService.createApplication(
              method,
              applicationConfigFile,
              applicationConfigFile.comment,
              {
                onUploadProgress,
                onUploadComplete,
              },
            );
            break;
          case "change":
            method = chunk => parseChunck(chunk, "change", canBeShowAndReturnErrorsMessage);
            await services.applicationService.changeConfiguration(
              method,
              applicationConfigFile,
              applicationConfigFile.comment,
              {
                onUploadProgress,
                onUploadComplete,
              },
            );
            break;
          case "validate":
            method = chunk => parseChunck(chunk, "validate", canBeShowAndReturnErrorsMessage);
            await services.applicationService.validateConfiguration(method, applicationConfigFile, {
              onUploadProgress,
              onUploadComplete,
            });
            break;
        }
        finishUpload();
      }
      catch (error) {
        validate.value = false;
        finishUpload();
        changeErrorsMessages([]);
        checkMessageErrors(error);
      }
    }

    async function createApplication() {
      await saveApplication("create");
    }

    async function changeConfiguration() {
      await saveApplication("change");
    }

    async function testApplication() {
      disabled.value = false;
      changeErrorsMessages([]);
      await saveApplication("validate");
    }

    function showHelp() {
      window.open("https://doc.openadom.fr", "_blank");
    }

    function parseInfoChunck(chunk) {
      infos.value = chunk;
    }

    function parseErrorChunck(chunk, canBeShowAndReturnErrorsMessage) {
      validate.value = false;
      let errorType = chunk.errorType;
      let response = chunk.result;
      if (errorType === "ValidationError") {
        disabled.value = true;
        canBeShowAndReturnErrorsMessage.push({
          type: response.message,
          message: services.errorsService.getErrorsMessages(
            response.message,
            response.params,
            "errors-yaml.",
          ),
          title: t("errors-yaml." + response.message + ".title"),
          params: response.params,
        });
      }
      errorsMessages.value = canBeShowAndReturnErrorsMessage;
      errors.value = chunk;
    }

    function parseProgressChunck(chunk) {
      counter.value = chunk.result;
      watch(counter, value => value);
    }

    function parseChunck(chunk, caller, canBeShowAndReturnErrorsMessage) {
      switch (chunk.type) {
        case "REACTIVE_INFO":
          parseInfoChunck(chunk);
          break;
        case "REACTIVE_RESULT":
          parseResultChunck(chunk, caller, {
            applicationConfig: applicationConfigFile,
            props,
            currentUser,
            services,
            router,
            changeBtnUpdateConfig,
            disabled,
            validate,
            errorsMessages,
            t,
          });
          break;
        case "REACTIVE_PROGRESS":
          parseProgressChunck(chunk);
          break;
        default:
          parseErrorChunck(chunk, canBeShowAndReturnErrorsMessage);
      }
      // console.log("obtening chunk : ", chunk)
    }

    function checkMessageErrors(error: ErrorType) {
      if (error.httpResponseCode === HttpStatusCodes.BAD_REQUEST) {
        errorsMessages.value = services.errorsService.getErrorsMessages(
          error.content.validationCheckResults,
        );
      }
      else {
        services.alertService.toastServerError(error);
      }
    }

    return {
      createApplication,
      changeConfiguration,
      showHelp,
      testApplication,
      applicationConfigFile,
      btnUpdateConfig,
      errorsMessages,
      isLoading,
      counter,
      errors,
      disabled,
      currentUser,
      activeStep,
      validate,
    };
  },
};
</script>
